if (!customElements.get('inline-quick-add')) {
  class InlineQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.card = this.closest('.js-product-card');
      this.variantInput = this.querySelector('[data-iqa-variant-id]');
      this.submitBtn = this.querySelector('[data-iqa-submit]');
      if (!this.card || !this.variantInput || !this.submitBtn) return;

      this.variants = this.parseVariants();
      this.submitText = this.querySelector('[data-iqa-submit-text]');
      this.optionEls = Array.from(this.querySelectorAll('.inline-quick-add__option'));
      this.i18n = {
        addToCart: this.dataset.i18nAddToCart,
        soldOut: this.dataset.i18nSoldOut
      };

      const initialId = Number(this.dataset.iqaInitialVariant);
      const inputId = Number(this.variantInput.value);
      const initial = this.variants.find((v) => v.id === initialId)
        || this.variants.find((v) => v.id === inputId)
        || this.variants.find((v) => v.available)
        || this.variants[0];
      this.selected = initial ? initial.options.slice() : this.optionEls.map(() => null);

      this.refresh();

      this.triggers = Array.from(this.card.querySelectorAll('[data-inline-quick-add-trigger]'));
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));

      this.addEventListener('click', this.handleValueClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown);
      const closeBtn = this.querySelector('[data-inline-quick-add-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close(true));

      // Both events bubble from the theme's <product-form> after a successful add.
      this.addEventListener('on:cart:add', () => this.close());
      this.addEventListener('on:line-item:change', () => this.close());

      document.addEventListener('click', this.handleDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      if (this.triggers) {
        this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
      }
    }

    /**
     * Reads the variant data rendered by snippets/inline-quick-add.liquid.
     * @returns {Array<{id: number, available: boolean, options: string[]}>}
     */
    parseVariants() {
      const script = this.querySelector('[data-iqa-variants]');
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[inline-quick-add] Invalid variant data', error); // eslint-disable-line
        return [];
      }
    }

    handleTriggerClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    handleDocumentClick(evt) {
      if (!this.isOpen || this.contains(evt.target)) return;
      if (evt.target.closest('[data-inline-quick-add-trigger]')) return;
      this.close();
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape') this.close(true);
    }

    handleValueClick(evt) {
      const btn = evt.target.closest('[data-iqa-value]');
      if (!btn || btn.disabled) return;
      evt.preventDefault();

      const index = this.optionEls.indexOf(btn.closest('.inline-quick-add__option'));
      if (index < 0) return;

      this.selected[index] = btn.dataset.iqaValue;
      this.refresh();
    }

    refresh() {
      this.renderSelection();
      this.updateAvailability();
      this.updateVariant();
    }

    renderSelection() {
      this.optionEls.forEach((optionEl, index) => {
        const value = this.selected[index];
        const valueLabel = optionEl.querySelector('[data-iqa-selected-value]');
        if (valueLabel) valueLabel.textContent = value || '';
        optionEl.querySelectorAll('[data-iqa-value]').forEach((btn) => {
          const isSelected = btn.dataset.iqaValue === value;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-pressed', String(isSelected));
        });
      });
    }

    /**
     * Disables a value when no in-stock variant matches it combined with the choices
     * made in the options before it.
     */
    updateAvailability() {
      this.optionEls.forEach((optionEl, index) => {
        optionEl.querySelectorAll('[data-iqa-value]').forEach((btn) => {
          const candidate = this.selected.slice();
          candidate[index] = btn.dataset.iqaValue;
          const match = this.variants.find((variant) => {
            for (let i = 0; i <= index; i += 1) {
              if (candidate[i] != null && variant.options[i] !== candidate[i]) return false;
            }
            return variant.available;
          });
          btn.classList.toggle('is-unavailable', !match);
          btn.disabled = !match;
        });
      });
    }

    updateVariant() {
      const allChosen = this.selected.every((value) => value != null);
      const exact = allChosen
        ? this.variants.find((variant) => variant.options.every((opt, i) => opt === this.selected[i]))
        : null;

      if (exact) {
        this.variantInput.value = exact.id;
        this.submitBtn.disabled = !exact.available;
        this.setSubmitText(exact.available ? this.i18n.addToCart : this.i18n.soldOut);
      } else {
        this.submitBtn.disabled = true;
        this.setSubmitText(this.i18n.addToCart);
      }
    }

    setSubmitText(text) {
      if (this.submitText && text) this.submitText.textContent = text;
    }

    open() {
      document.querySelectorAll('inline-quick-add.is-open').forEach((panel) => {
        if (panel !== this) panel.close();
      });
      // product-form re-enables its button after an add, so re-sync with the current selection.
      this.updateVariant();
      this.hidden = false;
      this.isOpen = true;
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));

      const firstValue = this.querySelector('[data-iqa-value]:not(:disabled)');
      if (firstValue) firstValue.focus({ preventScroll: true });
    }

    /**
     * @param {boolean} [returnFocus=false] - Move focus back to the "+" trigger.
     */
    close(returnFocus = false) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (returnFocus === true && this.triggers[0]) this.triggers[0].focus();

      setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, InlineQuickAdd.CLOSE_TRANSITION_MS);
    }
  }

  InlineQuickAdd.CLOSE_TRANSITION_MS = 250;

  customElements.define('inline-quick-add', InlineQuickAdd);
}
